import React, { useRef, useState } from 'react';
import { createIssue, updateReport, deleteAttachment } from "./helpers";
import TextInput from "./textInput";
import FilesList from "./filesList";
import PendingFilesList from "./pendingFilesList";
import ReassignModal from "./reassignModal";
import OpenRequestButton from "../../shared_components/request_thread/OpenRequestButton";
import RequestThreadPanel from "../../shared_components/request_thread/RequestThreadPanel";

const Edit = ({ issue, report }) => {
  const [note, setNote] = useState(report?.note || '');
  const [impression, setImpression] = useState(report?.impression || '');
  const [suggestions, setSuggestions] = useState(report?.suggestions || '');
  const [files, setFiles] = useState([]);
  const [attachments, setAttachments] = useState(report?.file_attachments || []);
  const [showModal, setShowModal] = useState(false)
  const requestPanelRef = useRef(null);
  const fileInputRef = useRef(null);
  const [canOpenRequest, setCanOpenRequest] = useState(false);

  const handlePanelState = ({ canOpen }) => setCanOpenRequest(!!canOpen);

  // Keep the native file input's own FileList in step with our state, so the
  // widget never claims "No file chosen" while files are still pending.
  const syncFileInput = (nextFiles) => {
    if (!fileInputRef.current || typeof DataTransfer === 'undefined') return;

    try {
      const transfer = new DataTransfer();
      nextFiles.forEach((file) => transfer.items.add(file));
      fileInputRef.current.files = transfer.files;
    } catch (error) {
      // Some older browsers disallow assigning to input.files. The list we render
      // is what gets submitted, so falling back to a stale native widget is cosmetic.
    }
  };

  const handleFileChange = (event) => {
    const nextFiles = [...files, ...Array.from(event.target.files || [])];
    setFiles(nextFiles);
    syncFileInput(nextFiles);
  };

  const handleRemoveFile = (index) => {
    const nextFiles = files.filter((_, i) => i !== index);
    setFiles(nextFiles);
    syncFileInput(nextFiles);
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      const response = await deleteAttachment(issue, report, attachmentId);
      if (response.data.success) {
        setAttachments((prev) => prev.filter((attachment) => attachment.id !== attachmentId));
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      const message = error.response?.data?.error || 'Failed to delete attachment';
      alert(message);
    }
  };

  const handleSubmit = async (type) => {
    const formData = new FormData();
    formData.append('consultation_report[note]', note)
    formData.append('consultation_report[impression]', impression)
    formData.append('consultation_report[suggestions]', suggestions)
    Array.from(files).forEach((file) => {
      formData.append('consultation_report[files][]', file);
    });
    formData.append('type', type)

    let confirmed = false

    const missingFields = impression.length < 1 || suggestions.length < 1

    if (missingFields) {
      window.alert('Please make sure Impression and Suggestions are filled out')
    }

    if (type == 'Send Consultation' && !missingFields) {
      confirmed = window.confirm("I have reviewed my report and ready to submit it to the requesting physician.")
    }

    if (type == 'Save Draft') { confirmed = true }

    if (confirmed) {
      try {
        let response;
        if (report && report.id) {
          response = await updateReport(issue, report, formData);
        } else {
          response = await createIssue(issue, formData);
        }
        window.location.href = response.data.path;
      } catch (error) {
        console.error('Error updating report:', error);
      }
    }
  };

  const handleOpenRequestClick = () => {
    requestPanelRef.current?.openPrompt();
  };

  return <>
    <ReassignModal showModal={showModal} setShowModal={setShowModal} issue={issue}/>
    <div className="relative flex flex-col break-words w-full bg-white">
      <div className="rounded-t mb-0 py-3 border-0">
        <div className="flex flex-wrap items-center">
          <div
            className="relative w-full flex flex-col md:flex-row items-center md:justify-center max-w-full flex-grow flex-1 text-slate-700 gap-y-2">
            <h3 className="uppercase font-semibold text-center w-full text-xl">
              Consultation Report
            </h3>
          </div>
        </div>
      </div>
      <div className="bg-slate-100 p-4 gap-y-4 flex flex-col text-base w-full">
        <TextInput title={'Impression'} label={impression} setLabel={setImpression}/>
        <TextInput title={'Note'} label={note} setLabel={setNote} optional/>
        <TextInput title={'Suggestions'} label={suggestions} setLabel={setSuggestions}/>
        <div className="flex flex-col items-start gap-x-1 mb-1">
          <label className="mb-1 font-semibold text-slate-600 uppercase">Upload Images</label>
          <input type="file" multiple ref={fileInputRef} onChange={handleFileChange}/>
          <PendingFilesList files={files} onRemove={handleRemoveFile}/>
        </div>
        <FilesList attachments={attachments} onDelete={handleDeleteAttachment} />
        <RequestThreadPanel
          ref={requestPanelRef}
          viewer={{ type: 'specialist' }}
          issue={issue}
          onStateChange={handlePanelState}
        />
        <div className="p-4 flex flex-row items-center justify-center gap-x-4">
          {issue.is_open && canOpenRequest && (
            <OpenRequestButton onClick={handleOpenRequestClick} />
          )}
          <button className="btn btn-charcoal" onClick={() => setShowModal(true)}>
            Reassign
          </button>
          <button className="btn btn-export" onClick={() => handleSubmit('Save Draft')}>
            Save Draft
          </button>
          <button className="btn btn-default" onClick={() => handleSubmit('Send Consultation')}>
            Send Consultation
          </button>
        </div>
      </div>
    </div>
  </>
};

export default Edit;
